import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, Disclaimer } from "@/components/shared";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function AdminGuardrails() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [disclaimer, setDisclaimer] = useState("");
  const [restricted, setRestricted] = useState("");
  const [busy, setBusy] = useState(false);
  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    api.get("/admin/ai-settings").then((r) => {
      const v = r.data.value || {};
      setSettings(r.data);
      setPrompt(v.system_prompt || "");
      setDisclaimer(v.default_disclaimer || "");
      setRestricted((v.restricted_topics || []).join(", "));
    });
  }, []);
  if (!settings) return <Spinner />;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/ai-settings", { system_prompt: prompt || null, default_disclaimer: disclaimer,
        restricted_topics: restricted.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("AI guardrails updated.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="Safety" title="AI Prompt & Guardrail Manager" subtitle="Centrally manage the AI Family Navigator's behaviour and boundaries." />
      {!isSuper && <div className="mb-4"><Disclaimer>Only super admins can edit these settings. You have read-only access.</Disclaimer></div>}
      <div className="kid-card p-6 space-y-5">
        <div className="space-y-1.5">
          <Label>System prompt <span className="text-xs text-kid-muted font-normal">leave blank to use the built-in safe default</span></Label>
          <textarea rows={10} value={prompt} onChange={(e) => setPrompt(e.target.value)} disabled={!isSuper}
            className="w-full rounded-xl border border-kid-line p-3 text-sm font-mono" data-testid="guardrails-prompt" placeholder="Using built-in default guarded prompt…" />
        </div>
        <div className="space-y-1.5">
          <Label>Default disclaimer</Label>
          <textarea rows={2} value={disclaimer} onChange={(e) => setDisclaimer(e.target.value)} disabled={!isSuper}
            className="w-full rounded-xl border border-kid-line p-3 text-sm" data-testid="guardrails-disclaimer" />
        </div>
        <div className="space-y-1.5">
          <Label>Restricted topics (comma separated)</Label>
          <input value={restricted} onChange={(e) => setRestricted(e.target.value)} disabled={!isSuper}
            className="w-full h-11 rounded-xl border border-kid-line px-3 text-sm" data-testid="guardrails-restricted" />
        </div>
        {isSuper && <button onClick={save} disabled={busy} className="kid-btn kid-btn-primary h-11 px-6" data-testid="guardrails-save">{busy ? "Saving…" : "Save guardrails"}</button>}
      </div>
    </div>
  );
}
